import Button from "@/common/components/Button";
import Modal from "@/common/components/Modal";
import { ModalMessageSimple } from "@/common/components/ModalMessageSimple";
import SelectController from "@/common/components/SelectController";
import { EstablishmentCompanyData } from "@/common/types/establishment/company.type";
import { useEstablishmentNotificationsConfigurationsRegisterNotifications } from "@/features/establishments/hooks/politics/useEstablishmentNotificationConfigurationsRegisterConfigurations";
import { getNotificationProvidersOptionsFromPossibleProviders } from "@/features/notifications/constants/providers.constant";
import { notificationsConfigurationsSchema, NotificationsConfigurationsValidationSchema } from "@/features/notifications/schemas/update.schema";
import { updateNotificationsConfigurationsDefaultValues } from "@/features/notifications/services/updateNotificationsConfigurationsDefaultValues";
import { NotificationConfigurationsAvailableNotificationData, NotificationConfigurationsData } from "@/features/notifications/types/configurations.type";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { SubmitHandler, useForm } from "react-hook-form";

interface UpdateNotificationModalProps {
  establishmentCompany?: EstablishmentCompanyData
  availableNotifications?: NotificationConfigurationsAvailableNotificationData[]
  configurations?: Record<string, NotificationConfigurationsData>
}

const UpdateNotificationsModal = ({ establishmentCompany, availableNotifications, configurations }: UpdateNotificationModalProps) => {
  const [isOpen, setIsOpen] = useState(false);

  const queryClient = useQueryClient();

  const [messages, setMessages] = useState<string[] | boolean>(false)

  const {
    mutate: registerNotifications,
    isPending: isRegisterNotificationsPending,
  } = useEstablishmentNotificationsConfigurationsRegisterNotifications({
    companyId: establishmentCompany?.id,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['establishmentCompanyNotificationConfigurationsAvailableNotifications', establishmentCompany?.id] });
      queryClient.invalidateQueries({ queryKey: ['establishmentCompanyNotificationConfigurations', establishmentCompany?.id] });
      setIsOpen(false);
    },
    onError: (error) => {
      if (error.response?.data.errors.base)
        setMessages(error.response?.data.errors.base)
    },
  });

  const { control, handleSubmit, reset, formState: { errors } } = useForm<NotificationsConfigurationsValidationSchema>({
    resolver: zodResolver(notificationsConfigurationsSchema),
    defaultValues: updateNotificationsConfigurationsDefaultValues(availableNotifications || [], configurations),
  });

  const onSubmit: SubmitHandler<NotificationsConfigurationsValidationSchema> = (data) => registerNotifications(data);

  useEffect(() => reset(updateNotificationsConfigurationsDefaultValues(availableNotifications || [], configurations)), [isOpen]);

  return (
    <>
      <Modal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        Trigger={(
          <Button
            name="open_update_fee_details_modal"
            operationKlassToCheckPermission="Admin::Establishment::CompanyNotificationConfigurations::RegisterConfigurations"
            className="h-[2.125rem] text-sm text-white-800"
          >
            Editar
          </Button>
        )}
        title="Editando notificações"
        useCancellableFooter
        disabled={isRegisterNotificationsPending}
        confirmButtonIsLoading={isRegisterNotificationsPending}
        onConfirm={handleSubmit(onSubmit)}
        confirmButtonClassName="bg-blue-700 rounded-br-lg"
        cancelButtonClassName="bg-white-200 rounded-bl-lg"
        bodyClassName='min-h-[360px] max-h-[calc(90vh-72px-48px)] overflow-auto'
        body={(
          <div className="flex flex-col gap-4 text-white-800">
            {availableNotifications?.map((notification) => (
              <div key={notification.notification_klass}>
                <SelectController
                  control={control}
                  name={notification.notification_klass}
                  label={notification.display_name}
                  options={getNotificationProvidersOptionsFromPossibleProviders(notification.possible_providers)}
                  errorMessage={errors[notification.notification_klass]?.message || undefined}
                />
              </div>
            ))}
          </div>
        )}
      />

      {messages && (
        <ModalMessageSimple
          messages={messages}
          setMessages={setMessages}
          isError
        />
      )}
    </>
  )
}

export default UpdateNotificationsModal;
